// Utils.ts
import { format, isValid, parseISO } from "date-fns";
const get = (obj: any, path: string, defaultValue: any = undefined): any => {
  const keys = path.split('.');
  return keys.reduce((acc, key) => (acc && acc[key] !== undefined? acc[key] : defaultValue), obj);
};

const set = (obj: any, path: string, value: any): void => {
  const keys = path.split('.');
  const lastKey = keys.pop();

  if (!lastKey) {
    return;
  }

  const nestedObj = keys.reduce((acc, key) => {
    if (!acc[key]) {
      acc[key] = {};
    }
    return acc[key];
  }, obj);

  nestedObj[lastKey] = value;
};

const getLookupItemKey = (item: any) => {
  if (Array.isArray(item)) {
    return item[0];
  }

  return item?.key ?? item?.value ?? item?.id ?? item?.PKey ?? item?.Code;
};

const getLookupItemLabel = (item: any, fallback: any) => {
  if (Array.isArray(item)) {
    return item[1] ?? fallback;
  }

  return item?.label ?? item?.name ?? item?.text ?? item?.Description ?? item?.Name ?? item?.value ?? fallback;
};

const resolveLookupValue = (lookupData: any, key: any) => {
  if (lookupData === undefined || lookupData === null || key === undefined || key === null) {
    return key;
  }

  if (Array.isArray(lookupData)) {
    const match = lookupData.find((item: any) => getLookupItemKey(item) === key);
    return match ? getLookupItemLabel(match, key) : key;
  }

  if (typeof lookupData === 'object') {
    const value = lookupData[key];
    return getLookupItemLabel(value, value ?? key);
  }

  return key;
};

const formatDateValue = (value: any, dateFormat = "MM/dd/yyyy") => {
  if (!value) {
    return "";
  }

  const parsedDate = value instanceof Date
    ? value
    : typeof value === "number"
      ? new Date(value)
      : parseISO(value);

  if (!isValid(parsedDate)) {
    return "";
  }

  return format(parsedDate, dateFormat);
};

const applyTemplateFormatter = (value: any, formatterExpression?: string) => {
  if (!formatterExpression) {
    return value ?? '';
  }

  const normalizedFormatterExpression = formatterExpression.toLowerCase();

  if (normalizedFormatterExpression === 'date') {
    return formatDateValue(value);
  }

  if (normalizedFormatterExpression.startsWith('date:')) {
    return formatDateValue(value, formatterExpression.substring(5) || undefined);
  }

  return value ?? '';
};

const evaluateTemplate = (template: string, data: any, root: any) => {
  return template.replace(/\${([^|}]+)(?:\|([^}]+))?}/g, (match, expression, formatter) => {
    expression = expression.trim();

    if (root && expression.startsWith('root.')) {
      return applyTemplateFormatter(get(root, expression.substring(5).trim()), formatter);
    }

    if (root && expression.startsWith('lookup.')) {
      const [, lookupKey, ...dataPathParts] = expression.split('.');
      const dataPath = dataPathParts.join('.');
      const key = get(data, dataPath);
      const lookupData = get(root, lookupKey);

      return applyTemplateFormatter(resolveLookupValue(lookupData, key), formatter);
    }

    return applyTemplateFormatter(get(data, expression), formatter);
  });
}

const evaluateExpression = (expr: string, context: any): any => {
  return new Function(...Object.keys(context), `return ${expr};`)(...Object.values(context));
}

const isEmpty = (data: any) => {
  // Check for undefined or null
  if (!data) {
    return true;
  }

  // Check for an empty array
  if (Array.isArray(data) && data.length === 0) {
    return true;
  }

  // Check for an object with no properties
  if (typeof data === 'object' && Object.keys(data).length === 0) {
    return true;
  }

  // For all other cases, return false
  return false;
}

const removeEmptyProperties = (obj: Record<string, any>) => {
  for (const key in obj) {
    if (obj[key] === null || obj[key] === undefined) {
      delete obj[key]; // Remove property if it's null or undefined
    }
  }
};

// Function to get the first element
const firstElement = (data: any) => {
  if (Array.isArray(data)) {
      if (data.length === 0) {
          data.push({});
      }
      return data[0];
  }
  return data;
};

const isObject = (value: any) => typeof value === 'object' && value !== null;

export { get, set, evaluateTemplate, evaluateExpression, formatDateValue, isEmpty, isObject, removeEmptyProperties, firstElement };
